import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';

export interface ReturnPolicy {
  id: string;
  storeId: string | null;
  name: string;
  windowDaysStandard: number;
  windowDaysPerishable: number;
  windowDaysSaleItem: number;
  cashierRefundCap: number;
  supervisorRefundCap: number;
  managerRefundCap: number;
  allowOfferLockedReturn: boolean;
  requireOfferOverridePin: boolean;
  allowCrossDayCashRefund: boolean;
  allowCrossStoreReturn: boolean;
  isReturnsEnabled: boolean;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  createdById: string | null;
  blockedCategories: Array<{ id: string; name: string }>;
  blockedDepartments: Array<{ id: string; name: string }>;
  blockedBrands: Array<{ id: string; name: string }>;
}

export interface UpsertReturnPolicyPayload {
  storeId?: string | null;
  name?: string;
  windowDaysStandard: number;
  windowDaysPerishable: number;
  windowDaysSaleItem: number;
  cashierRefundCap: number;
  supervisorRefundCap: number;
  managerRefundCap: number;
  allowOfferLockedReturn: boolean;
  requireOfferOverridePin: boolean;
  allowCrossDayCashRefund: boolean;
  allowCrossStoreReturn: boolean;
  isReturnsEnabled: boolean;
  blockedCategoryIds: string[];
  blockedDepartmentIds: string[];
  blockedBrandIds: string[];
}

@Injectable({ providedIn: 'root' })
export class ReturnPolicyService {
  constructor(private http: HttpClient) {}

  /**
   * Fetch the active policy for a store. Passing storeId='' (or omitting it)
   * returns the global default. Backend resolves store-specific over global.
   */
  getActive(storeId: string | null): Observable<ReturnPolicy | null> {
    let params = new HttpParams();
    if (storeId) params = params.set('storeId', storeId);
    return this.http.get<ReturnPolicy | null>(environment.returnPolicy, { params });
  }

  /** History (all versions, including archived). Admin / manager only. */
  history(storeId: string | null): Observable<ReturnPolicy[]> {
    let params = new HttpParams();
    if (storeId) params = params.set('storeId', storeId);
    return this.http.get<ReturnPolicy[]>(environment.returnPolicyHistory, { params });
  }

  /** Upsert — archives prior active row for the same scope then inserts. */
  upsert(payload: UpsertReturnPolicyPayload): Observable<ReturnPolicy> {
    return this.http.post<ReturnPolicy>(environment.returnPolicy, payload);
  }

  patch(id: string, payload: Partial<UpsertReturnPolicyPayload>): Observable<ReturnPolicy> {
    return this.http.patch<ReturnPolicy>(`${environment.returnPolicy}/${id}`, payload);
  }

  archive(id: string): Observable<{ id: string; archived: boolean }> {
    return this.http.delete<{ id: string; archived: boolean }>(`${environment.returnPolicy}/${id}`);
  }

  /** Stores dropdown — same endpoint OffersService uses. */
  getStores(): Observable<any> {
    return this.http.get(environment.stores);
  }

  getCategories(): Observable<any> {
    return this.http.get(environment.productCategories);
  }

  getDepartments(): Observable<any> {
    return this.http.get(environment.departments);
  }

  // Brand blocklist will land alongside a /brands endpoint (no controller
  // today). The backend already accepts blockedBrandIds; the UI sends an
  // empty array so a future PR just adds the picker without DTO changes.
}
